import React, { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch } from '../constants';
import ActionButton from '../components/ui/ActionButton';
import KpiCard from '../components/ui/KpiCard';

interface Mapping {
  account_key: string;
  account_code: string;
  account_name: string | null;
  account_type: string | null;
  confidence: number | null;
  classifier_source: string | null;
  reasoning: string | null;
  mapped_by: string | null;
  mapped_at: string | null;
  confirmed_by: string | null;
  confirmed_at: string | null;
}

interface Coverage {
  required_count: number;
  mapped_count: number;
  unmapped_count: number;
  low_confidence_count: number;
  unmapped: string[];
  low_confidence: { account_key: string; account_code: string; confidence: number | null }[];
}

interface CoaAccount {
  account_code: string;
  account_name: string;
  account_type: string | null;
}

const API = `${API_V1}/coa-mapping`;

/**
 * CoA Role Mappings -- AI-native binding between canonical AccountKey
 * roles (AP_TRADE, GRNI, DEP_EXPENSE_PPE, etc.) and the client's actual
 * chart-of-accounts. Replaces the broken standard_subtype + alphabetical
 * resolver model with explicit, user-visible, deterministic mappings.
 */
export default function CoARoleMappings() {
  const [mappings, setMappings] = useState<Mapping[]>([]);
  const [coverage, setCoverage] = useState<Coverage | null>(null);
  const [coaAccounts, setCoaAccounts] = useState<CoaAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editCode, setEditCode] = useState('');
  const [filter, setFilter] = useState<'ALL' | 'CONFIRMED' | 'FLAGGED'>('ALL');
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const showToast = (m: string, ok: boolean) => { setToast({ msg: m, ok }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mres, cres, ares] = await Promise.all([
        authFetch(`${API}/`),
        authFetch(`${API}/coverage`),
        authFetch(`${API_V1}/coa/accounts?limit=2000`),
      ]);
      if (mres.ok) { const d = await mres.json(); setMappings(d.mappings || []); }
      if (cres.ok) { setCoverage(await cres.json()); }
      if (ares.ok) {
        const d = await ares.json();
        const items = Array.isArray(d) ? d : (d.accounts || d.items || []);
        setCoaAccounts(items);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const runClassifier = async (withAI: boolean) => {
    setRunning(true);
    try {
      const res = await authFetch(`${API}/${withAI ? 'classify-ai' : 'classify'}`, { method: 'POST' });
      if (res.ok) {
        const d = await res.json();
        showToast(`Classifier ran: ${d.mapped_high_confidence || 0} mapped, ${d.flagged_for_review || 0} flagged, ${d.unmatched_count || 0} unmatched${withAI ? ` (${d.ai_classified || 0} via AI)` : ''}`, true);
        load();
      } else {
        const e = await res.json();
        showToast(e.detail || 'Classifier failed', false);
      }
    } catch { showToast('Classifier failed', false); }
    setRunning(false);
  };

  const confirmMapping = async (key: string) => {
    try {
      const res = await authFetch(`${API}/${encodeURIComponent(key)}/confirm`, { method: 'POST' });
      if (res.ok) { showToast(`Confirmed ${key}`, true); load(); }
    } catch { showToast('Confirm failed', false); }
  };

  const saveOverride = async (key: string) => {
    if (!editCode) { showToast('Pick an account first', false); return; }
    try {
      const res = await authFetch(`${API}/${encodeURIComponent(key)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_code: editCode }),
      });
      if (res.ok) { showToast(`Mapped ${key} -> ${editCode}`, true); setEditingKey(null); setEditCode(''); load(); }
      else { const e = await res.json(); showToast(e.detail || 'Save failed', false); }
    } catch { showToast('Save failed', false); }
  };

  const deleteMapping = async (key: string) => {
    if (!window.confirm(`Remove mapping for ${key}? Resolver falls back to legacy lookup until re-classified.`)) return;
    try {
      const res = await authFetch(`${API}/${encodeURIComponent(key)}`, { method: 'DELETE' });
      if (res.ok) { showToast(`Removed ${key}`, true); load(); }
    } catch { showToast('Delete failed', false); }
  };

  const filtered = mappings.filter(m => {
    if (filter === 'CONFIRMED') return !!m.confirmed_at;
    if (filter === 'FLAGGED') return (m.confidence ?? 1) < 0.95 && !m.confirmed_at;
    return true;
  });

  return (
    <div style={{ padding: '0 0 24px 0' }}>
      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 20, padding: '12px 18px', borderRadius: 6, background: toast.ok ? '#10b981' : '#ef4444', color: 'white', zIndex: 1000, fontSize: 13, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
          {toast.msg}
        </div>
      )}

      <div style={{ marginBottom: 12, color: 'var(--text-secondary)', fontSize: 13 }}>
        Each canonical role the platform needs (AP, GRNI, Depreciation Expense, etc.) maps to one of your chart-of-accounts. Auto-classified from account names with confidence scores; confirm or override any flagged below. Every JE the platform writes uses these mappings.
      </div>

      {coverage && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 }}>
          <KpiCard label="Required Roles" value={coverage.required_count} valueColor="var(--text-primary)" />
          <KpiCard label="Mapped" value={coverage.mapped_count} valueColor="var(--positive)" />
          <KpiCard label="Low Confidence" value={coverage.low_confidence_count} valueColor="var(--warning)" />
          <KpiCard label="Unmapped" value={coverage.unmapped_count} valueColor="var(--negative)" />
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <ActionButton label={running ? 'Running...' : 'Re-run Classifier'} onClick={() => runClassifier(false)} variant="secondary" disabled={running} />
        <ActionButton label={running ? 'Running...' : 'Run AI Classifier'} onClick={() => runClassifier(true)} variant="primary" disabled={running} title="Sends unmatched accounts to Claude for classification" />
        <div style={{ flex: 1 }} />
        <select value={filter} onChange={e => setFilter(e.target.value as any)} style={{ padding: '6px 10px', border: '1px solid var(--border-subtle)', borderRadius: 6, fontSize: 13, background: 'var(--bg-card)', color: 'var(--text-primary)' }}>
          <option value="ALL">All ({mappings.length})</option>
          <option value="CONFIRMED">Confirmed only</option>
          <option value="FLAGGED">Needs review (low confidence)</option>
        </select>
      </div>

      {coverage && coverage.unmapped_count > 0 && (
        <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 12, color: '#78350f' }}>
          <b>{coverage.unmapped_count} unmapped role{coverage.unmapped_count > 1 ? 's' : ''}:</b>{' '}
          {coverage.unmapped.slice(0, 8).join(', ')}{coverage.unmapped.length > 8 ? `, +${coverage.unmapped.length - 8} more` : ''}.{' '}
          Click <b>Run AI Classifier</b> to auto-fill, or override below.
        </div>
      )}

      <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--bg-subtle, #f8fafc)', borderBottom: '1px solid var(--border-subtle)' }}>
              <th style={th}>Role (AccountKey)</th>
              <th style={th}>Account</th>
              <th style={th}>Account Name</th>
              <th style={th}>Source</th>
              <th style={th}>Confidence</th>
              <th style={th}>Status</th>
              <th style={{ ...th, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ padding: 30, textAlign: 'center', color: 'var(--text-hint)' }}>Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: 30, textAlign: 'center', color: 'var(--text-hint)' }}>No mappings match filter.</td></tr>
            ) : filtered.map(m => {
              const isEditing = editingKey === m.account_key;
              const isExpanded = expandedKey === m.account_key;
              const conf = m.confidence ?? 1;
              return (
                <React.Fragment key={m.account_key}>
                  <tr style={{ borderBottom: '1px solid var(--border-subtle)', cursor: m.reasoning ? 'pointer' : 'default' }} onClick={() => m.reasoning && setExpandedKey(isExpanded ? null : m.account_key)}>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: 12, fontWeight: 600 }}>{m.account_key}</td>
                    <td style={{ ...td, fontFamily: 'monospace' }} onClick={e => e.stopPropagation()}>
                      {isEditing ? (
                        <select value={editCode} onChange={e => setEditCode(e.target.value)} style={{ padding: '4px 6px', border: '1px solid var(--border-subtle)', borderRadius: 4, fontSize: 12, width: 280 }}>
                          <option value="">Pick account...</option>
                          {coaAccounts.map(a => <option key={a.account_code} value={a.account_code}>{a.account_code} - {a.account_name}</option>)}
                        </select>
                      ) : m.account_code}
                    </td>
                    <td style={{ ...td, color: 'var(--text-secondary)' }}>{m.account_name || <span style={{ color: '#ef4444', fontWeight: 600 }}>NOT IN COA!</span>}</td>
                    <td style={td}><span style={{ fontSize: 11, padding: '2px 6px', background: '#e0e7ff', borderRadius: 3, color: '#3730a3' }}>{m.classifier_source || '-'}</span></td>
                    <td style={{ ...td, color: conf >= 0.95 ? '#10b981' : conf >= 0.8 ? '#f59e0b' : '#ef4444', fontWeight: 600 }}>
                      {m.confidence != null ? `${(m.confidence * 100).toFixed(0)}%` : '-'}
                    </td>
                    <td style={td}>
                      {m.confirmed_at
                        ? <span style={{ fontSize: 11, padding: '2px 8px', background: '#d1fae5', color: '#065f46', borderRadius: 12, fontWeight: 600 }}>CONFIRMED</span>
                        : conf < 0.95
                          ? <span style={{ fontSize: 11, padding: '2px 8px', background: '#fef3c7', color: '#92400e', borderRadius: 12, fontWeight: 600 }}>REVIEW</span>
                          : <span style={{ fontSize: 11, padding: '2px 8px', background: '#e0e7ff', color: '#3730a3', borderRadius: 12, fontWeight: 600 }}>AUTO</span>}
                    </td>
                    <td style={{ ...td, textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                      {isEditing ? (
                        <>
                          <ActionButton label="Save" onClick={() => saveOverride(m.account_key)} variant="primary" size="sm" />{' '}
                          <ActionButton label="Cancel" onClick={() => { setEditingKey(null); setEditCode(''); }} variant="ghost" size="sm" />
                        </>
                      ) : (
                        <>
                          {!m.confirmed_at && <><ActionButton label="Confirm" onClick={() => confirmMapping(m.account_key)} variant="success" size="sm" />{' '}</>}
                          <ActionButton label="Change" onClick={() => { setEditingKey(m.account_key); setEditCode(m.account_code); }} variant="secondary" size="sm" />
                        </>
                      )}
                    </td>
                  </tr>
                  {isExpanded && m.reasoning && (
                    <tr style={{ background: 'var(--bg-subtle, #f8fafc)' }}>
                      <td colSpan={7} style={{ padding: '8px 14px', fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                        <b>Reasoning:</b> {m.reasoning} {m.mapped_at && <span style={{ marginLeft: 12, color: 'var(--text-hint)' }}>· mapped {new Date(m.mapped_at).toLocaleString()}</span>}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const th: React.CSSProperties = { textAlign: 'left', padding: '10px 12px', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', letterSpacing: 0.5, textTransform: 'uppercase' };
const td: React.CSSProperties = { padding: '8px 12px', verticalAlign: 'middle' };
